import {useLocale} from '../../../../../provider/locale/locale-hook';

import * as styles from './aspect-scatter.scss';

export type ScatterPointType = {
    name: string;
    tonalityIdx: number;
    mentions: number;
    isOwn: boolean;
};

const WIDTH = 760;
const HEIGHT = 420;
const MARGIN_TOP = 24;
const MARGIN_RIGHT = 24;
const MARGIN_BOTTOM = 44;
const MARGIN_LEFT = 64;
const PLOT_WIDTH = WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
const PLOT_HEIGHT = HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
// Tonality index runs on the full [-100..+100] scale, so the X axis must span it
// with the neutral midpoint at 0 — otherwise negative indices collapse to the left edge.
const INDEX_MIN = -100;
const INDEX_MAX = 100;
const INDEX_MID = 0;

function niceCeil(value: number): number {
    if (value <= 0) {
        return INDEX_MAX;
    }

    const magnitude = 10 ** Math.floor(Math.log10(value));

    return Math.ceil(value / magnitude) * magnitude;
}

export function AspectScatter({points}: {points: Array<ScatterPointType>}): JSX.Element {
    const {getLocalizedString} = useLocale();

    const mentionsMax = niceCeil(Math.max(1, ...points.map((point) => point.mentions)));
    const mentionsMid = mentionsMax / 2;

    function toX(value: number): number {
        const clamped = Math.min(Math.max(value, INDEX_MIN), INDEX_MAX);

        return MARGIN_LEFT + ((clamped - INDEX_MIN) / (INDEX_MAX - INDEX_MIN)) * PLOT_WIDTH;
    }

    function toY(value: number): number {
        return MARGIN_TOP + PLOT_HEIGHT - (Math.min(Math.max(value, 0), mentionsMax) / mentionsMax) * PLOT_HEIGHT;
    }

    const midX = toX(INDEX_MID);
    const midY = toY(mentionsMid);
    const plotRight = MARGIN_LEFT + PLOT_WIDTH;
    const plotBottom = MARGIN_TOP + PLOT_HEIGHT;

    const mentionsLabel = getLocalizedString('REVIEWS_ANALYSIS__ASPECTS_TABLE__MENTIONS_COUNT');
    const indexLabel = getLocalizedString('REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX');

    return (
        <svg role="img" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%">
            {/* quadrant tints: laggards / leaders / outsiders / promising */}
            <rect
                className={styles.AspectScatter_quadrantLaggards}
                height={midY - MARGIN_TOP}
                width={midX - MARGIN_LEFT}
                x={MARGIN_LEFT}
                y={MARGIN_TOP}
            />
            <rect
                className={styles.AspectScatter_quadrantLeaders}
                height={midY - MARGIN_TOP}
                width={plotRight - midX}
                x={midX}
                y={MARGIN_TOP}
            />
            <rect
                className={styles.AspectScatter_quadrantOutsiders}
                height={plotBottom - midY}
                width={midX - MARGIN_LEFT}
                x={MARGIN_LEFT}
                y={midY}
            />
            <rect
                className={styles.AspectScatter_quadrantPromising}
                height={plotBottom - midY}
                width={plotRight - midX}
                x={midX}
                y={midY}
            />

            <text className={styles.AspectScatter_labelLaggards} fontSize={12} x={MARGIN_LEFT + 8} y={MARGIN_TOP + 16}>
                {getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__QUADRANT__LAGGARDS')}
            </text>
            <text
                className={styles.AspectScatter_labelLeaders}
                fontSize={12}
                textAnchor="end"
                x={plotRight - 8}
                y={MARGIN_TOP + 16}
            >
                {getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__QUADRANT__LEADERS')}
            </text>
            <text className={styles.AspectScatter_labelOutsiders} fontSize={12} x={MARGIN_LEFT + 8} y={plotBottom - 8}>
                {getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__QUADRANT__OUTSIDERS')}
            </text>
            <text
                className={styles.AspectScatter_labelPromising}
                fontSize={12}
                textAnchor="end"
                x={plotRight - 8}
                y={plotBottom - 8}
            >
                {getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__QUADRANT__PROMISING')}
            </text>

            {/* mid dividers + axes */}
            <line
                className={styles.AspectScatter_grid}
                strokeDasharray="4 4"
                x1={midX}
                x2={midX}
                y1={MARGIN_TOP}
                y2={plotBottom}
            />
            <line
                className={styles.AspectScatter_grid}
                strokeDasharray="4 4"
                x1={MARGIN_LEFT}
                x2={plotRight}
                y1={midY}
                y2={midY}
            />
            <line
                className={styles.AspectScatter_axis}
                x1={MARGIN_LEFT}
                x2={plotRight}
                y1={plotBottom}
                y2={plotBottom}
            />
            <line
                className={styles.AspectScatter_axis}
                x1={MARGIN_LEFT}
                x2={MARGIN_LEFT}
                y1={MARGIN_TOP}
                y2={plotBottom}
            />

            {/* axis titles */}
            <text
                className={styles.AspectScatter_axisLabel}
                fontSize={12}
                textAnchor="middle"
                x={MARGIN_LEFT + PLOT_WIDTH / 2}
                y={HEIGHT - 10}
            >
                {indexLabel}
            </text>
            <text
                className={styles.AspectScatter_axisLabel}
                fontSize={12}
                textAnchor="middle"
                transform={`rotate(-90 16 ${MARGIN_TOP + PLOT_HEIGHT / 2})`}
                x={16}
                y={MARGIN_TOP + PLOT_HEIGHT / 2}
            >
                {mentionsLabel}
            </text>

            {/* points */}
            {points.map((point) => {
                const pointX = toX(point.tonalityIdx);
                const pointY = toY(point.mentions);
                const label = point.isOwn
                    ? getLocalizedString('REVIEWS_ANALYSIS__ASPECT_LOCATION__OWN_COMPANY')
                    : point.name;

                return (
                    <g key={`${point.isOwn ? 'own' : point.name}-${point.tonalityIdx}-${point.mentions}`}>
                        <title>{`${label}\n${mentionsLabel}: ${point.mentions}\n${indexLabel}: ${point.tonalityIdx}`}</title>
                        <circle
                            className={
                                point.isOwn ? styles.AspectScatter_point__own : styles.AspectScatter_point__other
                            }
                            cx={pointX}
                            cy={pointY}
                            r={6}
                        />
                        <text
                            className={
                                point.isOwn
                                    ? styles.AspectScatter_pointLabel__own
                                    : styles.AspectScatter_pointLabel__other
                            }
                            fontSize={12}
                            x={pointX + 10}
                            y={pointY + 4}
                        >
                            {label}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}
